import { useState } from 'react';
import type { Service, Staff } from '@salonomy/types';
import { Button } from '@salonomy/ui';
import { RecurringSeriesModal } from './RecurringSeriesModal.js';

/**
 * WS7 phase 3 — the "make recurring" affordance for the New-appointment flow. A small inline control
 * the diary's new-booking modal mounts; clicking it opens the RecurringSeriesModal prefilled from the
 * in-progress booking (same staff / service / time / client), turning a one-off into a standing
 * weekly or fortnightly booking in one step.
 *
 * Integrate: drop <MakeRecurringButton .../> into NewAppointmentModal's body (e.g. beneath the time/
 * service row), passing the modal's current field values via `current`. It is self-contained — it owns
 * its own modal state and the series mutation — so the host booking flow needs no other changes. When
 * the operator creates the series here, close the host new-appointment modal (the standing booking
 * already created the first occurrence, so don't also create a single appointment).
 *
 * RBAC: render this only where the host already allows creating a booking — it inherits the host's
 * gating. The series endpoint itself requires `appointments:write`, mirroring single-booking create.
 */
export function MakeRecurringButton({
  date,
  tz,
  chairs,
  services,
  current,
  onCreated,
  label = 'Make recurring…',
}: {
  date: string;
  tz: string;
  chairs: Staff[];
  services: Service[];
  /** The in-progress booking values, used to prefill the series creator. */
  current?: {
    staffId?: string;
    serviceId?: string;
    time?: string;
    clientDisplayName?: string;
    clientId?: string | null;
  };
  /** Called after a series is created+dismissed so the host can close its own new-booking modal. */
  onCreated?: () => void;
  label?: string;
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button variant="ghost" size="sm" onClick={() => setOpen(true)}>
        {label}
      </Button>
      {open ? (
        <RecurringSeriesModal
          date={date}
          tz={tz}
          chairs={chairs}
          services={services}
          defaults={current}
          onClose={() => {
            setOpen(false);
            onCreated?.();
          }}
        />
      ) : null}
    </>
  );
}
